import { useState } from "react";
import axios from "axios";
import { Send, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import Reveal, { Eyebrow } from "./Reveal";
import { useLang } from "../i18n/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const EMPTY = { prenom: "", nom: "", entreprise: "", email: "", objet: "", message: "" };

const inputBase =
  "w-full rounded-sm border border-slate-300 bg-white px-4 py-3 text-sm text-slate-900 placeholder-slate-400 transition-colors duration-200 focus:outline-none focus:border-[#1B3A5B] focus:ring-2 focus:ring-[#1B3A5B]/15";

export default function ContactForm() {
  const { t, lang } = useLang();
  const f = t.contact.form;
  const [values, setValues] = useState(EMPTY);
  const [honeypot, setHoneypot] = useState("");
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | success

  const handleChange = (e) => {
    const { name, value } = e.target;
    setValues((v) => ({ ...v, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = () => {
    const errs = {};
    ["prenom", "nom", "objet", "message"].forEach((k) => {
      if (!values[k].trim()) errs[k] = f.required;
    });
    if (!values.email.trim()) errs.email = f.required;
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) errs.email = f.invalidEmail;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setStatus("sending");
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, {
        ...values,
        lang,
        website: honeypot,
      });
      setStatus("success");
      setValues(EMPTY);
      toast.success(f.successTitle, { description: f.success });
    } catch (err) {
      setStatus("idle");
      if (err?.response?.status === 429) {
        toast.error(f.rateLimited);
      } else {
        toast.error(f.error);
      }
    }
  };

  if (status === "success") {
    return (
      <div
        data-testid="contact-form-success"
        className="bg-[#F8FAFC] border border-slate-200 rounded-sm p-10 text-center"
      >
        <div className="mx-auto h-16 w-16 rounded-full bg-[#C9A15A]/15 flex items-center justify-center">
          <CheckCircle2 className="h-8 w-8 text-[#C9A15A]" />
        </div>
        <h3 className="mt-6 font-serif-display text-2xl font-bold text-[#1B3A5B]">
          {f.successTitle}
        </h3>
        <p className="mt-3 text-slate-600 max-w-md mx-auto">{f.success}</p>
        <button
          data-testid="contact-form-reset"
          onClick={() => setStatus("idle")}
          className="mt-8 inline-flex items-center gap-2 border border-[#1B3A5B] text-[#1B3A5B] hover:bg-[#1B3A5B] hover:text-white transition-colors duration-300 px-6 py-3 text-sm font-medium rounded-sm"
        >
          {f.submit}
        </button>
      </div>
    );
  }

  return (
    <Reveal>
      <div className="bg-white border border-slate-200 rounded-sm p-6 sm:p-8 md:p-10 shadow-sm">
        <Eyebrow>{f.eyebrow}</Eyebrow>
        <h2 className="mt-4 font-serif-display text-2xl sm:text-3xl font-bold tracking-tight text-[#1B3A5B]">
          {f.title}
        </h2>

        <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate data-testid="contact-form">
          {/* Honeypot field — hidden from humans, catches bots */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-9999px", opacity: 0, height: 0, overflow: "hidden" }}>
            <label>
              Website
              <input
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
              />
            </label>
          </div>
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {f.prenom} <span className="text-[#C9A15A]">*</span>
              </label>
              <input
                type="text"
                name="prenom"
                value={values.prenom}
                onChange={handleChange}
                data-testid="contact-input-prenom"
                className={inputBase}
              />
              {errors.prenom && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="error-prenom">
                  {errors.prenom}
                </p>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {f.nom} <span className="text-[#C9A15A]">*</span>
              </label>
              <input
                type="text"
                name="nom"
                value={values.nom}
                onChange={handleChange}
                data-testid="contact-input-nom"
                className={inputBase}
              />
              {errors.nom && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="error-nom">
                  {errors.nom}
                </p>
              )}
            </div>
          </div>

          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {f.entreprise}{" "}
                <span className="text-slate-400 font-normal">{f.entrepriseHint}</span>
              </label>
              <input
                type="text"
                name="entreprise"
                value={values.entreprise}
                onChange={handleChange}
                data-testid="contact-input-entreprise"
                className={inputBase}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {f.email} <span className="text-[#C9A15A]">*</span>
              </label>
              <input
                type="email"
                name="email"
                value={values.email}
                onChange={handleChange}
                data-testid="contact-input-email"
                className={inputBase}
              />
              {errors.email && (
                <p className="mt-1.5 text-xs text-red-600" data-testid="error-email">
                  {errors.email}
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {f.objet} <span className="text-[#C9A15A]">*</span>
            </label>
            <input
              type="text"
              name="objet"
              value={values.objet}
              onChange={handleChange}
              data-testid="contact-input-objet"
              className={inputBase}
            />
            {errors.objet && (
              <p className="mt-1.5 text-xs text-red-600" data-testid="error-objet">
                {errors.objet}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">
              {f.message} <span className="text-[#C9A15A]">*</span>
            </label>
            <textarea
              name="message"
              rows={5}
              value={values.message}
              onChange={handleChange}
              data-testid="contact-input-message"
              className={`${inputBase} resize-y`}
            />
            {errors.message && (
              <p className="mt-1.5 text-xs text-red-600" data-testid="error-message">
                {errors.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={status === "sending"}
            data-testid="contact-submit-button"
            className="inline-flex items-center justify-center gap-2 bg-[#C9A15A] text-white hover:bg-[#B38C48] disabled:opacity-60 disabled:cursor-not-allowed transition-colors duration-300 px-8 py-3.5 text-sm font-medium rounded-sm w-full sm:w-auto"
          >
            {status === "sending" ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {f.sending}
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                {f.submit}
              </>
            )}
          </button>
        </form>
      </div>
    </Reveal>
  );
}
